import React, { useCallback, useContext, useState } from "react";
import { graphql } from "react-relay";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import ThemeContext from "~/common/ThemeContext";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { ReactComponent as ThumbsUpDark } from "./css/gave-feedback-thumbs-up-dark.svg";
import { ReactComponent as ThumbsUp } from "./css/gave-feedback-thumbs-up.svg";
import { ReuseFeedbackQuestions as ReuseQuestions } from "./ReuseQuestions";

import type { GaveFeedbackModalNewFeedbackMutation as NewFeedbackType } from "~/__generated__/GaveFeedbackModalNewFeedbackMutation.graphql";

import styles from "./css/GaveFeedbackModal.module.css";

interface Props {
	id: string;
	hasAccessToFeedback: boolean;
	isRequest?: boolean;
}

export const NewFeedbackMutation = graphql`
	mutation GaveFeedbackModalNewFeedbackMutation {
		createDraftFeedback(type: REQUESTED) {
			feedback {
				id
			}
		}
	}
`;

export default withRouter(function GaveFeedbackModal({
	id,
	history,
	location,
	isRequest,
	hasAccessToFeedback,
}: Props & RouteComponentProps) {
	const [modalOpen, setModalOpen] = useState(true);
	const [reuseOpen, setReuseOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const { isDarkMode } = useContext(ThemeContext);

	const newFeedback = useCallback(async () => {
		if (!hasAccessToFeedback) return showFeatureModal("feedback");

		setLoading(true);

		const { createDraftFeedback } = await commitMutationPromise<NewFeedbackType>(environment, {
			mutation: NewFeedbackMutation,
			variables: {},
		});

		if (createDraftFeedback) {
			history.push(`/feedback/${createDraftFeedback.feedback.id}/draft/`);
		} else {
			toast.error("Failed to create new feedback");
		}
	}, [hasAccessToFeedback, history]);

	const reuseQuestions = useCallback(
		(feedbackID: string) => {
			if (feedbackID) history.push(`/feedback/${feedbackID}/draft/`);
		},
		[history],
	);

	const closeModal = useCallback(() => {
		delete location.state;
		history.replace(location);
		setModalOpen(false);
	}, [history, location]);

	const openReuse = useCallback(() => {
		setModalOpen(false);
		setReuseOpen(true);
	}, []);

	const closeReuse = useCallback(() => {
		setReuseOpen(false);
		setModalOpen(true);
	}, []);

	let header = "Thanks for giving feedback!";
	let body = "While you're at it, are you interested in asking for feedback?";

	if (isRequest) {
		header = "Your feedback request has been sent!";
		body = "While you're at it, would you like to start a new request?";
	}

	return (
		<>
			<Modal isOpen={modalOpen} onClose={closeModal}>
				<Modal.Header onClose={closeModal}>
					<Modal.Title>{header}</Modal.Title>
				</Modal.Header>
				<Modal.Body className={styles.main}>
					{isDarkMode ? <ThumbsUpDark /> : <ThumbsUp />}
					<p>{body}</p>
				</Modal.Body>
				<Modal.Footer>
					{hasAccessToFeedback && (
						<Button size="normal" disabled={loading} variant="primary" onClick={openReuse}>
							Re-use questions…
						</Button>
					)}
					<Button
						size="normal"
						disabled={loading}
						variant={!hasAccessToFeedback ? "billing" : "primary"}
						onClick={newFeedback}
					>
						Start new feedback {!hasAccessToFeedback && <FontAwesomeIcon icon={billingUpgradeIconDef} />}
					</Button>
				</Modal.Footer>
			</Modal>
			<ReuseQuestions id={id} open={reuseOpen} onClose={closeReuse} onComplete={reuseQuestions} />
		</>
	);
});
